"use client";

import type React from "react";

import { Button } from "../button/button.web";
import { ArrowLeft, X } from "lucide-react";
import {
  motion,
  AnimatePresence,
  useDragControls,
  type PanInfo,
} from "framer-motion";
import {
  Fragment,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { useBodyScrollLock } from "../../hooks/use-body-scroll-lock";
import { useFocusTrap } from "../../hooks/use-focus-trap";
import { hasScrollableAncestor } from "../../styles/scrollable-ancestor";
import { usePullToRefreshLock } from "../../hooks/use-pull-to-refresh-lock";
import { PULL_TO_REFRESH_IGNORE_ATTR } from "../../styles/pull-to-refresh-lock";
import {
  useMediaQuery,
  DESKTOP_MODAL_MEDIA_QUERY,
} from "../../hooks/use-media-query";
import { SCROLL_LOCK_SCROLLABLE_ATTR } from "../../styles/body-scroll-lock";
import { createPortal } from "react-dom";
import { cn } from "../../cn";
import { Z_OVERLAY } from "../../styles/z-layers";
import { useSidebarOpenState } from "../sidebar/sidebar.web";
import { REUSABLE_MODAL_COPY } from "./reusable-modal.copy";

export interface ReusableModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string | React.ReactNode;
  description?: string | React.ReactNode;
  children: React.ReactNode;
  showBackButton?: boolean;
  showCloseButton?: boolean;
  /** Classe do Tailwind (`max-w-lg`) ou valor CSS (`28rem`). Vale só no desktop. */
  maxWidth?: string;
  actions?: React.ReactNode;
  containerId?: string;
  className?: string;
  canCloseBackdrop?: boolean;
  contentClassName?: string;
  wrapperClassName?: string;
  contenTFloating?: React.ReactNode;
  showTitle?: boolean;
  showFooter?: boolean;
  /**
   * Quando o filho gerencia o próprio scroll (ex.: editores com scroll interno),
   * passe `false` para o modal NÃO criar um segundo container rolável.
   * Default `true` (comportamento atual: o conteúdo do modal rola).
   */
  scrollableContent?: boolean;
  /** Altura máxima do bottom sheet no mobile (ex.: `100dvh` para editor em tela cheia). */
  mobileSheetMaxHeight?: string;
  /** Ajusta a altura do bottom sheet ao conteúdo, respeitando `mobileSheetMaxHeight`. */
  mobileSheetFitContent?: boolean;
  /** Ajusta a altura do modal desktop ao conteúdo, com scroll quando passar do limite. */
  fitContent?: boolean;
  /** Altura máxima quando `fitContent` está ativo (desktop e mobile). */
  fitContentMaxHeight?: string;
  /**
   * z-index do overlay no mobile. O padrão é `Z_OVERLAY`; um modal aberto de
   * dentro de outro passa `Z_OVERLAY_NESTED` (ver `lib/z-layers.ts`).
   */
  mobileOverlayClassName?: string;
  /** Centraliza o overlay na viewport inteira (ex.: pickers aninhados sobre outro modal). */
  overlayFullViewport?: boolean;
  /**
   * Alinhamento vertical do painel no desktop. **`center` é o padrão.**
   *
   * Era `start` — o painel encostado no topo da área útil. Isso deixava um vão
   * embaixo e nenhum em cima, e cada modal que reparava passava
   * `desktopAlign="center"` na mão: dez arquivos faziam isso, e **nenhum**
   * passava `start`. Ou seja, ninguém escolhia o padrão; quem estava nele
   * estava por omissão. Em 02/09/2026 sobravam doze modais de altura fixa
   * pendurados no topo por esse motivo, e o padrão foi invertido.
   *
   * `start` continua disponível para quem quiser de fato ancorar no topo. O
   * overlay rola quando o painel passa da viewport, nos dois modos.
   */
  desktopAlign?: "start" | "center";
  /** Anima mudanças de tamanho do painel (ex.: modal que expande por aba). */
  animateLayout?: boolean;
  /** Empilha ações em coluna com largura total no desktop (ex.: modais de detalhe). */
  stackedActions?: boolean;
  /**
   * Cabeçalho na escala da folha do celular (`p-4`, título `text-lg`) também no
   * diálogo de desktop. É para o diálogo curto — a confirmação —, onde a escala
   * de modal de tela cheia faz o título competir com a pergunta.
   *
   * Só o cabeçalho: o rodapé continua decidido por `stackedActions`, porque
   * empilhar os botões é outra escolha e não anda junto com esta.
   */
  compactHeader?: boolean;
  /**
   * Classe do rodapé das ações — para quem separa o rodapé do corpo (borda e
   * fundo próprios), como o painel de filtros no diálogo.
   */
  footerClassName?: string;
  /**
   * `data-testid` do painel do modal — o mesmo valor no desktop e na folha
   * inferior do mobile, porque para o bot é o mesmo diálogo. Sem isto, todo
   * modal do produto só era alcançável por texto, que é o que o lint de
   * cenários proíbe.
   */
  testId?: string;
}

const SHEET_ACCESSIBLE_TITLE_FALLBACK = "Modal";
const MOBILE_SHEET_TITLE_ID = "reusable-modal-a11y-title";
const SHEET_TRANSITION = { duration: 0.3, ease: [0.32, 0.72, 0, 1] as const };
const SHEET_DRAG_CLOSE_OFFSET_PX = 72;
const SHEET_DRAG_CLOSE_VELOCITY = 400;

interface MobileBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  canCloseBackdrop: boolean;
  title: string | React.ReactNode;
  description?: string | React.ReactNode;
  contenTFloating?: React.ReactNode;
  mobileSheetMaxHeight?: string;
  mobileSheetFitContent?: boolean;
  overlayClassName?: string;
  modalChromeProps: Omit<ModalChromeProps, "children" | "isCompact">;
  testId?: string;
  children: React.ReactNode;
}

function MobileBottomSheet({
  isOpen,
  onClose,
  canCloseBackdrop,
  title,
  description,
  contenTFloating,
  mobileSheetMaxHeight = "90dvh",
  mobileSheetFitContent = false,
  overlayClassName = Z_OVERLAY,
  modalChromeProps,
  testId,
  children,
}: MobileBottomSheetProps) {
  const [isPresent, setIsPresent] = useState(isOpen);
  const [isClosing, setIsClosing] = useState(false);
  const dragControls = useDragControls();
  const panelRef = useRef<HTMLDivElement>(null);

  const sheetAccessibleDescription = getSheetAccessibleDescription(description);

  /**
   * Contém o scroll por toque ao conteúdo rolável do sheet. Sem isso, arrastar
   * em áreas não-roláveis (cabeçalho, toolbar) faz o iOS "panear" o documento.
   */
  useEffect(() => {
    if (!isPresent) {
      return;
    }
    const panel = panelRef.current;
    if (!panel) {
      return;
    }
    const onTouchMove = (event: TouchEvent) => {
      if (!hasScrollableAncestor(event.target, panel)) {
        event.preventDefault();
      }
    };
    panel.addEventListener("touchmove", onTouchMove, { passive: false });
    return () => {
      panel.removeEventListener("touchmove", onTouchMove);
    };
  }, [isPresent]);

  const finishClose = useCallback(() => {
    setIsPresent(false);
    setIsClosing(false);
  }, []);

  /**
   * Gesto, Escape e "×" só **pedem** para fechar; quem decide é o `isOpen` do
   * pai, como no diálogo do desktop e no `Modal` nativo. A saída anima pelo
   * efeito abaixo quando o `isOpen` vira `false`. Antes a folha se fechava
   * sozinha e avisava depois — um pai que recusava (`ConfirmModal` carregando,
   * pedido de acesso do suporte) ficava com `isOpen` em `true` e a folha sumida.
   */
  const requestClose = useCallback(() => {
    if (isClosing) {
      return;
    }
    onClose();
  }, [isClosing, onClose]);

  useEffect(() => {
    if (isOpen) {
      setIsPresent(true);
      setIsClosing(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen && isPresent && !isClosing) {
      setIsClosing(true);
    }
  }, [isOpen, isPresent, isClosing]);

  useBodyScrollLock(isPresent);
  usePullToRefreshLock(isPresent);

  useEffect(() => {
    if (!isPresent) {
      return;
    }
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        requestClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isPresent, requestClose]);

  const handleDragEnd = (
    _event: MouseEvent | TouchEvent | PointerEvent,
    info: PanInfo,
  ) => {
    if (
      info.offset.y > SHEET_DRAG_CLOSE_OFFSET_PX ||
      info.velocity.y > SHEET_DRAG_CLOSE_VELOCITY
    ) {
      requestClose();
      return;
    }
  };

  if (!isPresent) {
    return null;
  }

  const handleBackdropPointerDown = (
    event: React.PointerEvent<HTMLDivElement>,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    if (canCloseBackdrop) {
      requestClose();
    }
  };

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key="reusable-modal-mobile-root"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className={cn(
          "fixed inset-0 overflow-hidden overscroll-none",
          overlayClassName,
        )}
        {...{ [PULL_TO_REFRESH_IGNORE_ATTR]: "" }}
      >
        <div
          className="absolute inset-0 bg-black/50"
          aria-hidden
          onPointerDown={handleBackdropPointerDown}
        />
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 flex flex-col justify-end"
          style={{
            maxHeight: mobileSheetMaxHeight,
            ...(mobileSheetFitContent ? {} : { height: mobileSheetMaxHeight }),
          }}
        >
          <motion.div
            ref={panelRef}
            data-testid={testId}
            role="dialog"
            aria-modal="true"
            aria-labelledby={MOBILE_SHEET_TITLE_ID}
            drag="y"
            dragControls={dragControls}
            dragListener={false}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.5 }}
            onDragEnd={handleDragEnd}
            initial={{ y: "100%" }}
            animate={{ y: isClosing ? "100%" : 0 }}
            transition={SHEET_TRANSITION}
            onAnimationComplete={() => {
              if (isClosing) {
                finishClose();
              }
            }}
            style={{
              maxHeight: mobileSheetMaxHeight,
              paddingBottom:
                "max(env(safe-area-inset-bottom), var(--vv-keyboard-inset, 0px))",
              ...(mobileSheetFitContent
                ? {}
                : { height: mobileSheetMaxHeight }),
            }}
            className={cn(
              "pointer-events-auto flex w-full flex-col overflow-hidden rounded-b-none rounded-t-2xl border-t border-border bg-card shadow-2xl",
              mobileSheetFitContent && "h-auto",
            )}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <p id={MOBILE_SHEET_TITLE_ID} className="sr-only">
              {getSheetAccessibleTitle(title)}
            </p>
            {sheetAccessibleDescription ? (
              <p className="sr-only">{sheetAccessibleDescription}</p>
            ) : null}
            {/*
              O "×" do bottom sheet sai daqui, e não do `showCloseButton`: a
              maioria das telas passa `showCloseButton` na mão, então mexer no
              padrão não tiraria o botão de quase ninguém. É também o que o
              `ui/sheet` já faz — lá o botão nasce desligado quando o lado é
              `bottom`.

              O que sustentava o "×" era alcance, não o gesto: arrastar e tocar
              no backdrop não existem para teclado nem leitor de tela. Esses
              dois seguem atendidos — Escape fecha, e o botão abaixo entra na
              ordem de foco sem ocupar espaço na tela.
            */}
            <button
              type="button"
              onClick={requestClose}
              // Mesmo id do app (`reusable-modal-fechar`): é por aqui que o
              // regressivo fecha o que abriu (TAM-310). Um pixel transparente
              // no canto, e não `sr-only`: o `clip` do sr-only tira o botão do
              // hit-test, e a alça de arrastar ficava por cima dele.
              data-testid="reusable-modal-fechar"
              className="absolute top-2 right-2 z-10 h-px w-px overflow-hidden whitespace-nowrap opacity-0 focus-visible:h-auto focus-visible:w-auto focus-visible:opacity-100 focus-visible:rounded-md focus-visible:bg-card focus-visible:px-3 focus-visible:py-1.5 focus-visible:ring-2 focus-visible:ring-ring"
            >
              {REUSABLE_MODAL_COPY.close}
            </button>
            <div
              className="flex shrink-0 cursor-grab touch-none justify-center py-3.5 active:cursor-grabbing"
              onPointerDown={(e) => dragControls.start(e)}
              aria-hidden
            >
              <div className="h-1.5 w-12 rounded-full bg-muted-foreground/35" />
            </div>
            {contenTFloating}
            <div
              className={cn(
                "relative flex min-h-0 min-w-0 flex-col overflow-hidden",
                !mobileSheetFitContent && "flex-1",
              )}
            >
              <ModalChrome
                {...modalChromeProps}
                isCompact
                showCloseButton={false}
                fitContent={mobileSheetFitContent}
                fitContentMaxHeight={mobileSheetMaxHeight}
                onClose={requestClose}
              >
                {children}
              </ModalChrome>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

function getSheetAccessibleTitle(title: string | React.ReactNode): string {
  if (typeof title === "string" && title.trim().length > 0) {
    return title;
  }
  return SHEET_ACCESSIBLE_TITLE_FALLBACK;
}

function getSheetAccessibleDescription(
  description?: string | React.ReactNode,
): string | undefined {
  if (typeof description === "string" && description.trim().length > 0) {
    return description;
  }
  return undefined;
}

/** Fragmento vazio (`<></>`) não descreve nada: não vira `aria-describedby`. */
function hasAccessibleDescription(description?: string | React.ReactNode): boolean {
  if (typeof description === "string") return description.trim().length > 0;
  return isValidElement(description) && description.type !== Fragment;
}

function useIsClientMounted(): boolean {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  return mounted;
}

interface ModalChromeProps {
  title: string | React.ReactNode;
  description?: string | React.ReactNode;
  children: React.ReactNode;
  showTitle: boolean;
  showFooter: boolean;
  scrollableContent?: boolean;
  showBackButton: boolean;
  showCloseButton: boolean;
  actions?: React.ReactNode;
  contentClassName?: string;
  onClose: () => void;
  isCompact: boolean;
  fitContent?: boolean;
  fitContentMaxHeight?: string;
  stackedActions?: boolean;
  compactHeader?: boolean;
  footerClassName?: string;
  /** Só no diálogo: ids que o painel aponta em `aria-labelledby`/`aria-describedby`. */
  titleId?: string;
  descriptionId?: string;
}

function ModalChrome({
  title,
  description,
  children,
  showTitle,
  showFooter,
  scrollableContent = true,
  showBackButton,
  showCloseButton,
  actions,
  contentClassName,
  onClose,
  isCompact,
  fitContent = false,
  fitContentMaxHeight = "92dvh",
  stackedActions = false,
  compactHeader = false,
  footerClassName,
  titleId,
  descriptionId,
}: ModalChromeProps) {
  const useStackedFooter = isCompact || stackedActions;
  const useCompactHeader = isCompact || compactHeader;
  return (
    <>
      {/* Este "×" é do modal de desktop. O bottom sheet mobile desliga o botão
          na chamada e resolve o alcance por teclado com um "Fechar" só de
          foco — arrastar para baixo já dá conta do toque. */}
      {showCloseButton && (
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          data-testid="reusable-modal-fechar"
          className="absolute top-2 right-2 z-10 h-8 w-8 rounded-full text-muted-foreground hover:bg-muted"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">{REUSABLE_MODAL_COPY.close}</span>
        </Button>
      )}

      {showTitle && (
        <div
          className={cn(
            "flex-shrink-0",
            useCompactHeader ? "p-4" : "p-5",
            showCloseButton && "pr-12",
          )}
        >
          {typeof title === "string" ? (
            <h2
              id={titleId}
              className={cn(
                "mb-1 font-bold text-foreground",
                useCompactHeader ? "text-lg" : "text-xl",
              )}
            >
              {title}
            </h2>
          ) : titleId ? (
            <div id={titleId}>{title}</div>
          ) : (
            title
          )}

          {typeof description === "string" ? (
            <p id={descriptionId} className="text-sm text-muted-foreground">
              {description}
            </p>
          ) : descriptionId && description ? (
            <div id={descriptionId}>{description}</div>
          ) : (
            description
          )}
        </div>
      )}

      {/* Sem título visível, o diálogo ainda precisa de nome: o leitor de
          tela anuncia pelo `aria-labelledby`. */}
      {!showTitle && titleId ? (
        <h2 id={titleId} className="sr-only">
          {getSheetAccessibleTitle(title)}
        </h2>
      ) : null}

      <div
        data-reusable-modal-content=""
        {...(fitContent || !scrollableContent
          ? {}
          : { [SCROLL_LOCK_SCROLLABLE_ATTR]: "" })}
        className={cn(
          "relative min-h-0 px-4 pb-4",
          !scrollableContent
            ? "flex-1 overflow-hidden"
            : fitContent
              ? "overflow-y-auto overscroll-y-contain [-webkit-overflow-scrolling:touch]"
              : "flex-1 overflow-y-auto overscroll-y-contain [-webkit-overflow-scrolling:touch]",
          contentClassName,
        )}
        style={
          fitContent
            ? { maxHeight: `calc(${fitContentMaxHeight} - 5.5rem)` }
            : undefined
        }
      >
        {children}
      </div>

      {showFooter && (
        <div
          className={cn(
            "flex-shrink-0 px-4 pb-4",
            useStackedFooter
              ? "flex flex-col gap-3"
              : "flex items-center justify-between",
            footerClassName,
          )}
        >
          {showBackButton ? (
            <Button
              variant="outline"
              size="sm"
              onClick={onClose}
              className={cn("gap-2", useStackedFooter && "order-2 w-full")}
            >
              <ArrowLeft className="h-4 w-4" />
              {REUSABLE_MODAL_COPY.back}
            </Button>
          ) : !useStackedFooter ? (
            <div />
          ) : null}

          {actions && (
            <div
              className={cn(useStackedFooter ? "order-1 w-full" : "space-x-2")}
            >
              {actions}
            </div>
          )}
        </div>
      )}
    </>
  );
}

export function ReusableModal({
  isOpen,
  onClose,
  title,
  description,
  children,
  showTitle = true,
  showFooter = true,
  scrollableContent = true,
  showBackButton = false,
  showCloseButton = true,
  actions,
  containerId = "reusable-modal-root",
  className,
  maxWidth,
  canCloseBackdrop = true,
  contentClassName,
  wrapperClassName,
  contenTFloating,
  mobileSheetMaxHeight,
  mobileSheetFitContent,
  fitContent = false,
  fitContentMaxHeight = "85vh",
  mobileOverlayClassName,
  overlayFullViewport = false,
  desktopAlign = "center",
  animateLayout = false,
  stackedActions = false,
  compactHeader = false,
  footerClassName,
  testId,
}: ReusableModalProps) {
  const isClientMounted = useIsClientMounted();
  const isDesktop = useMediaQuery(DESKTOP_MODAL_MEDIA_QUERY);
  const open = useSidebarOpenState();
  const titleId = useId();
  const descriptionId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  // Só o diálogo: a folha tem o próprio `role="dialog"` e o "Fechar" de foco.
  // `isClientMounted` entra na condição porque o painel só existe depois da
  // montagem: um modal que já nasce aberto teria `active` verdadeiro desde a
  // primeira passada, com o painel ainda nulo, e o efeito não rodaria de novo
  // quando ele aparecesse — o foco ficaria no `body` (medido no harness do
  // desktop em 2026-09-15).
  useFocusTrap(panelRef, isOpen && isDesktop && isClientMounted);

  useBodyScrollLock(isOpen && isDesktop);
  usePullToRefreshLock(isOpen && isDesktop);

  useEffect(() => {
    if (!isOpen || !isDesktop) {
      return;
    }

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, isDesktop, onClose]);

  const handleBackdropClick = () => {
    if (canCloseBackdrop) {
      onClose();
    }
  };

  const modalChromeProps = {
    title,
    description,
    showTitle,
    showFooter,
    scrollableContent,
    showBackButton,
    showCloseButton,
    actions,
    contentClassName,
    onClose,
    fitContent,
    fitContentMaxHeight,
    stackedActions,
    compactHeader,
    footerClassName,
  };

  if (!isClientMounted) {
    return null;
  }

  if (!isDesktop) {
    return createPortal(
      <MobileBottomSheet
        isOpen={isOpen}
        onClose={onClose}
        canCloseBackdrop={canCloseBackdrop}
        title={title}
        description={description}
        contenTFloating={contenTFloating}
        mobileSheetMaxHeight={mobileSheetMaxHeight}
        mobileSheetFitContent={mobileSheetFitContent}
        overlayClassName={mobileOverlayClassName}
        modalChromeProps={modalChromeProps}
        testId={testId}
      >
        {children}
      </MobileBottomSheet>,
      document.body,
    );
  }

  // `maxWidth` aceita as duas formas que os chamadores já usam: classe do
  // Tailwind (`max-w-lg`, `max-w-[600px]`) ou valor CSS solto (`28rem`).
  const maxWidthClass = maxWidth?.startsWith("max-w-") ? maxWidth : undefined;
  const maxWidthStyle =
    maxWidth && !maxWidthClass ? { maxWidth } : undefined;

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          data-testid="reusable-modal-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className={cn(
            overlayFullViewport
              ? `fixed inset-0 ${Z_OVERLAY} flex h-screen w-full items-center justify-center bg-black/50 p-4 backdrop-blur-sm`
              : // `top-0` não é enfeite: sem ele o `fixed` usa a posição
                // estática como borda superior, então qualquer coisa
                // renderizada acima no fluxo (o aviso de assinatura pendente,
                // vencida ou cancelada) empurrava o overlay para baixo — e o
                // `h-screen` estourava a mesma medida por baixo, cortando o pé
                // de todo modal do produto.
                cn(
                  // O overlay do desktop estava em `z-50`, o mesmo valor de
                  // todo popover — valores iguais não empilham, e era por isso
                  // que a barra de ferramentas da imagem aparecia por cima do
                  // diálogo de recorte. Agora os dois degraus são distintos
                  // (ver `lib/z-layers.ts`), e o mobile usa o mesmo.
                  `fixed inset-y-0 right-0 ${Z_OVERLAY} flex justify-center bg-black/50 p-4 backdrop-blur-sm`,
                  // `overflow-y-auto` anda junto com o `items-center`: painel
                  // mais alto que a viewport, centralizado, teria as duas
                  // pontas cortadas sem um container que role.
                  desktopAlign === "center"
                    ? "items-center overflow-y-auto"
                    : "items-start",
                ),
            !overlayFullViewport &&
              (open
                ? "w-full lg:w-[calc(100vw-var(--sidebar-width))]"
                : "w-full"),
            wrapperClassName,
          )}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleBackdropClick();
            }
          }}
        >
          {contenTFloating && contenTFloating}

          <motion.div
            data-testid={testId}
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={
              hasAccessibleDescription(description) ? descriptionId : undefined
            }
            tabIndex={-1}
            layout={animateLayout}
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{
              duration: 0.25,
              ease: "easeOut",
              layout: { duration: 0.3, ease: [0.32, 0.72, 0, 1] },
            }}
            className={cn(
              "relative flex w-full flex-col overflow-y-hidden overflow-x-visible rounded-lg border border-border bg-card shadow-lg outline-none",
              // O padrão só entra quando ninguém pediu largura. `cn` não
              // consegue derrubar `lg:max-w-[50vw]` com um `max-w-lg` do
              // chamador — variantes diferentes não conflitam — e era por isso
              // que o prop `maxWidth` não fazia nada.
              !maxWidthClass && !maxWidthStyle && "sm:max-w-[95vw] lg:max-w-[50vw]",
              maxWidthClass,
              fitContent
                ? "h-auto max-h-[85vh]"
                : "h-full max-h-[80vh] lg:max-h-[85vh]",
              className,
            )}
            style={maxWidthStyle}
            onClick={(e) => e.stopPropagation()}
          >
            <ModalChrome
              {...modalChromeProps}
              isCompact={false}
              titleId={titleId}
              descriptionId={descriptionId}
            >
              {children}
            </ModalChrome>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (containerId === "body") {
    return createPortal(modalContent, document.body);
  }

  if (containerId) {
    const container = document.getElementById(containerId);
    if (container) {
      return createPortal(modalContent, container);
    }
  }

  return modalContent;
}
